import * as React from "react"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import { IconButton } from "@mui/material"
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Grid from "@mui/material/Grid2"
import { DataGrid, GridToolbar } from "@mui/x-data-grid"
import { moneyFormat } from "../../../Utils"

const columns = [
    {
        field: "name",
        headerName: "Name",
        flex: 2,
        minWidth: 250,
        editable: false,
        sortable: false,
    },
    {
        field: "sku",
        headerName: "SKU",
        flex: 1,
        minWidth: 150,
        editable: false,
        sortable: false,
        renderCell: ({ value }) => (
            <span>
                <IconButton size="small" onClick={() => navigator.clipboard.writeText(value)}>
                    <ContentCopyIcon />
                </IconButton>
                {value}
            </span>
        )
    },
    {
        field: "quantity",
        headerName: "Quantity",
        type: 'number',
        width: 120,
        editable: false,
        sortable: false,
        valueFormatter: (value) => value !== undefined ? value.toFixed(2) : '',
        renderCell: ({ formattedValue }) => (
            <span>
                <IconButton size="small" onClick={() => navigator.clipboard.writeText(formattedValue)}>
                    <ContentCopyIcon />
                </IconButton>
                {formattedValue}
            </span>
        )
    },
    {
        field: "retail",
        headerName: "Retail",
        type: 'number',
        width: 150,
        editable: false,
        sortable: false,
        valueFormatter: (value) => value !== undefined ? value.toFixed(2) : ''
    },
    {
        field: "billed",
        headerName: "Billed",
        type: 'number',
        width: 150,
        editable: false,
        sortable: false,
        valueFormatter: (value) => value !== undefined ? value.toFixed(2) : ''
    },
    {
        field: "tax",
        headerName: "Tax",
        type: 'number',
        width: 150,
        editable: false,
        sortable: false,
        valueFormatter: (value) => value !== undefined ? `${value.toFixed(2)}%` : ''
    },
    {
        field: "total",
        headerName: "Total",
        type: 'number',
        width: 150,
        editable: false,
        sortable: false,
        valueFormatter: (value) => value !== undefined ? value.toFixed(2) : ''
    }
]

export default function ItemizedInvoiceModal({
    open,
    closeModal,
    invoice,
    serviceOrder
}) {

    const table_data = []
    if (serviceOrder !== undefined) {
        let discounted_parts_table = {};
        (serviceOrder?.service_order_services ?? []).forEach((service) => {
            const declined = service.status === 'DECLINED'
            if (!declined) {
                if (service.official_flat_rate) {
                    table_data.push({
                        id: table_data.length,
                        name: `${service.name}: Flat Rate`,
                        sku: service.flat_rate_sku,
                        quantity: 1,
                        retail: undefined,
                        billed: service.official_flat_rate,
                        tax: service.taxable_flat_rate ? serviceOrder.location_tax_rate : undefined,
                        total: service.official_flat_rate,
                    })
                }
                if (service.official_hourly_rate && service.official_minutes_worked) {
                    table_data.push({
                        id: table_data.length,
                        name: `${service.name}: Hourly Rate`,
                        sku: service.hourly_rate_sku,
                        quantity: (service.official_minutes_worked / 60),
                        retail: undefined,
                        billed: service.official_hourly_rate,
                        tax: service.taxable_hourly_rate ? serviceOrder.location_tax_rate : undefined,
                        total: (service.official_hourly_rate * service.official_minutes_worked / 60),
                    })
                }
                service.service_order_parts.forEach((part) => {
                    if (part.status_type !== 'DECLINED') {
                        if (service.part_price_adjustment === -100 && part.actual_cost === 0) {
                            if (discounted_parts_table[service.part_coupon_sku] === undefined) {
                                discounted_parts_table[service.part_coupon_sku] = 0
                            }
                            discounted_parts_table[service.part_coupon_sku] += Math.round(part.cost * part.quantity * 100) / 100
                        }
                        table_data.push({
                            id: table_data.length,
                            name: `${part.name}`,
                            sku: part.sku,
                            quantity: part.quantity,
                            retail: part.cost,
                            billed: part.actual_cost,
                            tax: part.taxable ? serviceOrder.location_tax_rate : undefined,
                            total: (part.actual_cost * part.quantity),
                        })
                    }
                })
            }
        });
        const shop_fee_charge = (serviceOrder?.service_order_charges ?? []).find((x) => x.charge_type === 'SHOP_FEES')
        if (shop_fee_charge) {
            table_data.push({
                id: table_data.length,
                name: `Shop Fees`,
                sku: shop_fee_charge.sku,
                quantity: 1,
                retail: undefined,
                billed: shop_fee_charge.actual_cost,
                tax: shop_fee_charge.tax_rate ? shop_fee_charge.tax_rate : undefined,
                total: shop_fee_charge.actual_cost,
            })
        }
        (serviceOrder?.service_order_charges ?? []).filter((x) => x.charge_type === 'FEE').forEach((fee) => {
            table_data.push({
                id: table_data.length,
                name: `${fee.name}`,
                sku: fee.sku,
                quantity: 1,
                retail: undefined,
                billed: fee.actual_cost,
                tax: fee.tax_rate ? fee.tax_rate : undefined,
                total: fee.actual_cost,
            })
        });
        (serviceOrder?.service_order_charges ?? []).filter((x) => x.charge_type === 'ADJUSTMENT').forEach((adjustment) => {
            table_data.push({
                id: table_data.length,
                name: `Adjustment`,
                sku: adjustment.sku,
                quantity: 1,
                retail: undefined,
                billed: adjustment.actual_cost,
                tax: adjustment.tax_rate ? adjustment.tax_rate : undefined,
                total: adjustment.actual_cost,
            })
        })
        Object.keys(discounted_parts_table).forEach((discount) => {
            if (discounted_parts_table[discount] !== 0) {
                table_data.push({
                    id: table_data.length,
                    name: `Parts Discount Code`,
                    sku: discount,
                    quantity: -1,
                    retail: undefined,
                    billed: discounted_parts_table[discount],
                    tax: undefined,
                    total: discounted_parts_table[discount] * -1,
                })
            }
        })
    }

    return (
        <div>
            <Dialog
                open={open}
                onClose={() => closeModal(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                maxWidth='xl'
                fullWidth
            >
                <DialogTitle>
                    <span style={{ "paddingRight": "24px" }}>Itemized Invoice</span>
                    <IconButton
                        aria-label="close"
                        onClick={() => closeModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <DataGrid
                        rows={table_data}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{ toolbar: { csvOptions: { fileName: `ProfitMaxPro_Itemized_Invoice_${invoice.id}` } } }}
                        columns={columns}
                        autoHeight={true}
                        disableRowSelectionOnClick
                    />
                    <Grid container justifyContent={"flex-end"} alignItems={"center"} sx={{ textAlign: 'right', mr: 1, pr: 1 }}>
                        <Grid size={9}>
                            <span>Total Before Tax:</span>
                        </Grid>
                        <Grid size={3}>
                            <span>${moneyFormat(invoice.total_cost - invoice.tax)}</span>
                        </Grid>
                        <Grid size={9}>
                            <span>Tax:</span>
                        </Grid>
                        <Grid size={3}>
                            <span>${moneyFormat(invoice.tax)}</span>
                        </Grid>
                        <Grid size={9}>
                            <span>Total:</span>
                        </Grid>
                        <Grid size={3}>
                            <span>${moneyFormat(invoice.total_cost)}</span>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                </DialogActions>
            </Dialog>
        </div>
    )
}
